import { everyForCron, type ScheduleEvery } from "@nova-ai/shared"

import { humanizeEnumLabel } from "./enum-label"
import { isScheduledJobRunActive, type ScheduledJobRun } from "../services/use-scheduled-jobs"

const JOB_DISPLAY: Readonly<Record<string, { label: string; group: string }>> = {
  "automatic-preparation": { label: "Automatic preparation", group: "Orders" },
  "orders-sync": { label: "Orders sync", group: "Orders" },
  "outbound-events": { label: "Outbound events", group: "Orders" },
  "products-sync": { label: "Products sync", group: "Products" },
  "products-resnapshot": { label: "Products resnapshot", group: "Products" },
  "product-values-events": { label: "Product values events", group: "Products" },
  "product-values-audit": { label: "Product values audit", group: "Products" },
  "product-visual-events": { label: "Product visual events", group: "Product visuals" },
  "product-visual-audit": { label: "Product visual audit", group: "Product visuals" },
  "product-visual-pim-completion": {
    label: "Product visual PIM completion",
    group: "Product visuals",
  },
}
export const SCHEDULE_JOB_GROUPS = ["Orders", "Products", "Product visuals", "Other"] as const
export function scheduledJobLabel(id: string): string {
  return JOB_DISPLAY[id]?.label ?? humanizeEnumLabel(id.replaceAll("-", "_"))
}
export function scheduledJobGroup(id: string): string {
  return JOB_DISPLAY[id]?.group ?? "Other"
}
const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"]
const pad = (value: string | number) => String(value).padStart(2, "0")

/** Describe only expressions whose full meaning is known; preserve other cron verbatim. */
export function cadenceLabel(cron: string): string {
  const every = everyForCron(cron)
  if (every) {
    return scheduleEveryLabel(every)
  }
  const fields = cron.trim().split(/\s+/)
  const [minute, hour, day, month, weekday] = fields
  if (
    fields.length !== 5 ||
    !minute ||
    !hour ||
    day !== "*" ||
    month !== "*" ||
    !/^\d+$/.test(minute) ||
    Number(minute) > 59
  ) {
    return cron
  }
  return clockCadence(hour, minute, weekday ?? "") ?? cron
}

function clockCadence(hour: string, minute: string, weekday: string): string | null {
  const clock = (value: string) => `${pad(value)}:${pad(minute)}`
  if (/^\d+$/.test(hour) && Number(hour) < 24) {
    if (weekday === "*") {
      return `Daily at ${clock(hour)}`
    }
    if (/^[0-7]$/.test(weekday)) {
      return `${WEEKDAYS[Number(weekday) % 7]}s at ${clock(hour)}`
    }
  }
  if (weekday !== "*") {
    return null
  }
  const range = /^(\d+)-(\d+)$/.exec(hour)
  if (range?.[1] && range[2] && Number(range[1]) <= Number(range[2]) && Number(range[2]) < 24) {
    return `Hourly, ${clock(range[1])}–${clock(range[2])}`
  }
  if (/^\d+(,\d+)+$/.test(hour) && hour.split(",").every((value) => Number(value) < 24)) {
    return `Daily at ${hour.split(",").map(clock).join(", ")}`
  }
  return null
}

export function scheduleEveryLabel(every: ScheduleEvery): string {
  if (every === "1m") {
    return "Every minute"
  }
  if (every.endsWith("m")) {
    return `Every ${every.slice(0, -1)} minutes`
  }
  if (every === "1h") {
    return "Hourly"
  }
  if (every.endsWith("h")) {
    return `Every ${every.slice(0, -1)} hours`
  }
  return "Daily at 00:00"
}

export function formatScheduleLocalSlot(localSlot: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(localSlot)
  if (!match) {
    return localSlot
  }
  const [, year, month, day, hour, minute] = match
  return `${day}.${month}.${year}, ${hour}:${minute}`
}

export function formatRunDuration(run: ScheduledJobRun, now = new Date()): string | null {
  if (!run.startedAt) {
    return null
  }
  const startedAt = new Date(run.startedAt).getTime()
  const finishedAt = run.finishedAt ? new Date(run.finishedAt).getTime() : now.getTime()
  if (!Number.isFinite(startedAt) || !Number.isFinite(finishedAt) || finishedAt < startedAt) {
    return null
  }
  const totalSeconds = Math.floor((finishedAt - startedAt) / 1000)
  if (totalSeconds < 60) {
    return `${totalSeconds}s`
  }
  const totalMinutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  if (totalMinutes < 60) {
    return seconds === 0 ? `${totalMinutes}m` : `${totalMinutes}m ${seconds}s`
  }
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`
}

const RUN_TRIGGER_LABELS: Readonly<Record<string, string>> = {
  scheduled: "Scheduled",
  manual_ui: "Dashboard",
  manual_cli: "CLI",
  system: "System",
}

export function runTriggerLabel(trigger: string): string {
  return RUN_TRIGGER_LABELS[trigger] ?? humanizeEnumLabel(trigger)
}

export function runStatusLabel(run: ScheduledJobRun): string {
  return humanizeEnumLabel(run.effectiveStatus)
}

function productRunResultLabel(run: ScheduledJobRun): string | null {
  if (run.outcome && "kind" in run.outcome && run.outcome.kind === "products_sync") {
    const aggregate = run.outcome.aggregate
    return `${aggregate.succeeded}/${aggregate.storesTotal} stores · ${aggregate.changed} changed · ${aggregate.written} written`
  }
  if (run.effectiveStatus === "running" && run.workload?.product) {
    const progress = run.workload.product.progress
    return `${progress.storesDone}/${progress.storesTotal} stores`
  }
  return null
}

function eventPassResultLabel(run: ScheduledJobRun): string | null {
  if (run.effectiveStatus !== "succeeded") {
    return null
  }
  if (run.continuationReason) {
    return `Pass succeeded · ${run.continuationReason}${run.successorRunId ? " · linked successor" : " · no successor retained"}`
  }
  if (run.outcome && "kind" in run.outcome && run.outcome.kind === "product_values_events") {
    return `Pass succeeded · ${run.outcome.applied} applied · ${run.outcome.pendingRemaining} pending`
  }
  if (run.outcome && "kind" in run.outcome && run.outcome.kind === "product_visual_events") {
    return `Pass succeeded · ${run.outcome.pagesAdvanced} pages advanced · ${run.outcome.pendingJobs} jobs pending`
  }
  return null
}

export function runResultLabel(run: ScheduledJobRun): string {
  const eventLabel = eventPassResultLabel(run)
  if (eventLabel) {
    return eventLabel
  }
  const productLabel = productRunResultLabel(run)
  if (productLabel) {
    return productLabel
  }
  switch (run.effectiveStatus) {
    case "queued":
      return "Waiting"
    case "running":
      return "In progress"
    case "succeeded":
      return "Completed"
    case "failed":
      return run.failureSummary ?? "Run failed"
    case "interrupted":
      return run.failureSummary ?? "Run interrupted"
    case "coalesced":
      return run.coalescedIntoRunId
        ? `Attached to run ${run.coalescedIntoRunId}`
        : "Attached to another run"
    default:
      return humanizeEnumLabel(run.effectiveStatus)
  }
}

export function scheduledRunDetailLines(run: ScheduledJobRun): string[] {
  const lineage = [
    ...(run.predecessorRunId ? [`Continues run ${run.predecessorRunId}`] : []),
    ...(run.successorRunId ? [`Successor run ${run.successorRunId}`] : []),
  ]
  if (!run.outcome || !("kind" in run.outcome) || run.outcome.kind !== "products_sync") {
    return lineage
  }
  const identity = run.outcome.productJobId ?? run.workload?.id
  return [
    ...lineage,
    ...(identity ? [`Product job ${identity}`] : []),
    ...run.outcome.stores.flatMap((store) => {
      if (!store.ok) {
        return [`${store.store}: failed — ${store.error}`]
      }
      const lines = [
        `${store.store}: ${store.changed} changed · ${store.written}/${store.examined} written · ${store.missing} missing${store.resumed ? " · resumed" : ""}`,
      ]
      if (store.auditLogGaps) {
        const gaps = store.auditLogGaps
        lines.push(
          `${store.store} audit gaps: ${gaps.gapParents}/${gaps.comparedParents} parents · ${gaps.unhashedParents} unhashed · ${gaps.ambiguousParents} ambiguous${gaps.skipped ? ` · skipped: ${gaps.skipped}` : ""}${gaps.errors.length > 0 ? ` · errors: ${gaps.errors.join("; ")}` : ""}`
        )
      }
      if (store.absence) {
        const absence = store.absence
        lines.push(
          `${store.store} missing-product evidence: ${absence.flagged}/${absence.candidates} flagged${absence.refused ? " · refused" : ""}${absence.errors.length > 0 ? ` · errors: ${absence.errors.join("; ")}` : ""}`
        )
      }
      return lines
    }),
  ]
}

export function orderScheduledJobRuns(
  runs: readonly ScheduledJobRun[]
): readonly ScheduledJobRun[] {
  return runs.toSorted((left, right) => {
    const leftIsActive = isScheduledJobRunActive(left)
    const rightIsActive = isScheduledJobRunActive(right)
    if (leftIsActive !== rightIsActive) {
      return leftIsActive ? -1 : 1
    }
    const timeDifference =
      new Date(right.triggeredAt).getTime() - new Date(left.triggeredAt).getTime()
    if (timeDifference !== 0) {
      return timeDifference
    }
    if (left.id === right.id) {
      return 0
    }
    return left.id < right.id ? 1 : -1
  })
}

export function scheduleDuration(milliseconds: number | null): string {
  if (milliseconds === null) {
    return "—"
  }
  const seconds = Math.round(milliseconds / 1000)
  if (seconds < 60) {
    return `${seconds}s`
  }
  const minutes = Math.floor(seconds / 60)
  return seconds % 60 ? `${minutes}m ${seconds % 60}s` : `${minutes}m`
}

export function scheduleRelative(instant: string, timeZone: string, now = new Date()): string {
  const minutes = Math.round((Date.parse(instant) - now.getTime()) / 60_000)
  const absolute = Math.abs(minutes)
  if (absolute < 1) {
    return "now"
  }
  if (absolute < 1440) {
    const span =
      absolute < 60
        ? `${absolute} min`
        : `${Math.floor(absolute / 60)} h${absolute % 60 ? ` ${absolute % 60} min` : ""}`
    return minutes > 0 ? `in ${span}` : `${span} ago`
  }
  const days = Math.round(absolute / 1440)
  if (minutes < 0) {
    return days === 1 ? "yesterday" : `${days} days ago`
  }
  const date = new Date(instant)
  const day =
    days === 1
      ? "Tomorrow"
      : new Intl.DateTimeFormat("en", { weekday: "long", timeZone }).format(date)
  return `${day} ${new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit", timeZone }).format(date)}`
}

export function scheduleShortDate(instant: string, timeZone: string): string {
  const parts = new Intl.DateTimeFormat("en-GB", {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    timeZone,
  }).formatToParts(new Date(instant))
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? ""
  return `${get("weekday")} ${get("day")}.${get("month")}. ${get("hour")}:${get("minute")}`
}
